import React, {
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { translate } from '../Services/translation';
import { useSelector, useDispatch } from 'react-redux';
import { searchFacetChange } from '../Actions/FacetedSearch.action';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import ArrowDown from '../../images/bevego/arrow-down.svg';
import ArrowUp from '../../images/bevego/arrow-up.svg';
import Filter from '../../images/bevego/filter.svg';

const FacetedSearchGroup = ({ group, showFilters }) => {
    const [collapsed, setCollapsed] = useStateWithCallbackLazy(true);
    const showLessItemCount = useRef(10);
    const list = useRef(null);
    const showLess = useRef(null);
    const showMore = useRef(null);
    const listTop = useRef(null);
    const showLessBottom = useRef(null);
    const showMoreBottom = useRef(null);
    const firstRender = useRef(true);

    const updateHeights = useCallback(() => {
        const { top: listPosTop } = list.current.getBoundingClientRect();
        listTop.current = listPosTop;

        if (showLess.current) {
            showLessBottom.current = showLess.current.getBoundingClientRect().bottom;
        }
        if (showMore.current) {
            showMoreBottom.current = showMore.current.getBoundingClientRect().bottom;
        }
    }, []);

    const toggleShowMore = useCallback(() => {
        if (group.options.length === 0) {
            return;
        }
        setCollapsed(
            (prevState) => !prevState,
            (collapsed) => {
                list.current.style.height = `${collapsed ? 30 : showMoreBottom.current - listTop.current}px`;
            }
        );
    }, [setCollapsed, group.options.length]);

    const hideAll = () => {
        list.current.style.height = '30px';
        setCollapsed(true);
    };

    useEffect(() => {
        if (!firstRender.current) {
            updateHeights();
            return;
        }
        firstRender.current = false;

        updateHeights();
        hideAll();
    }, [group, updateHeights]);

    useEffect(() => {
        hideAll();
    }, [showFilters]);

    useEffect(() => {
        if (!collapsed && group.options.length === 0) {
            hideAll();
        } else if (!collapsed) {
            updateHeights();
            list.current.style.height = `${showMoreBottom.current - listTop.current}px`;
        }
    }, [group, collapsed, updateHeights]);

    return (
        <Fragment>
            <ul className="faceted-search__group" role="group" ref={list}>
                <div className="faceted-search__group-header" onClick={toggleShowMore}>
                    {group.label} ({group.options.length})
                    <span className="faceted-search__show-more">
                        {collapsed ? <ArrowDown /> : <ArrowUp />}
                    </span>
                </div>
                {group.options &&
                    group.options.map((item, itemIndex, array) => (
                        <li
                            key={`${item.label}-${itemIndex}`}
                            className="faceted-search__item"
                            role="menuitemcheckbox"
                            ref={(elem) => {
                                if (itemIndex === showLessItemCount.current - 2) {
                                    showLess.current = elem;
                                }
                                if (itemIndex === array.length - 1) {
                                    showMore.current = elem;
                                }
                            }}
                        >
                            <FacetedFilterCheckbox item={item} group={group} />
                        </li>
                    ))}
            </ul>
            <hr className="faceted-search__divider" />
        </Fragment>
    );
};

const FacetedFilterCheckbox = ({ item, group }) => {
    const dispatch = useDispatch();
    const onChange = useCallback(
        (group, item) => dispatch(searchFacetChange(group, item)),
        [dispatch]
    );
    return (
        <label className="faceted-filter">
            <input
                className="faceted-filter__input"
                type="checkbox"
                onChange={(event) => onChange(group, item)}
                checked={
                    group.selectedOptions != null &&
                    group.selectedOptions.includes(item.id)
                }
            />
            <span className="faceted-filter__label">{item.label}</span>
            {!isNaN(item.quantity) && item.quantity != null && (
                <span className="faceted-filter__quantity">
                    &nbsp;({item.quantity})
                </span>
            )}
        </label>
    );
};

const FacetedSearch = () => {
    const [showFilters, setShowFilters] = useState(false);
    const { facetFilters, navigationTheme } = useSelector(
        (state) => state.facetedSearch
    );
    const dispatch = useDispatch();

    return (
        navigationTheme !== 'category' && facetFilters && facetFilters.length > 0 && (
            <div className="faceted-search" role="menu">
                <div className="faceted-search__filter" onClick={() => setShowFilters(!showFilters)}>
                    <h4 className="faceted-search__filter--text">
                        {translate(`code.filters.hide`)}
                    </h4>
                    <Filter className="faceted-search__filter--icon" />
                </div>
                <hr className="faceted-search__divider" />
                {facetFilters.map((group, groupIndex) => (
                    <FacetedSearchGroup
                        key={`${group.label}-${groupIndex}`}
                        group={group}
                        showFilters={showFilters}
                    />
                ))}
            </div>
        )
    );
};

export default FacetedSearch;